/**
 * Hamburger menu module for Ragsmith Web UI
 */

import { state, resetNarratorState, saveSessionStateWithModel, setTemperature, setFidelity, setMemoryLevel, setAutoCompact, isContainerMode, clearContainerMode } from './state.js';
import { refreshContextMeter } from './context-meter.js';
import {
    elements,
    updateModeIndicators,
    addSystemMessage,
    clearChat
} from './ui.js';
import { loadAgentHandler, loadCollectionHandler } from './chat.js';
import { commands } from './commands.js';
import { refreshConversations, clearActiveConversation } from './conversations.js';
import { loadModel } from './api.js';
import { updateCurrentModelDisplay, openModelManager, loadSelectedModel, setToolingRequired } from './model-manager.js';
import { chooseModelSwitchScope } from './model-switch-dialog.js';
import { initAgentManager, openAgentManager } from './agent-manager.js';
import { initCollectionManager, openCollectionManager } from './collection-manager.js';
import { initKBManager, openKBManager, getKBCount } from './kb-manager.js';
import { initAgentKBManager, openAgentKBManager } from './agent-kb-manager.js';
import { initCollectionKBManager, openCollectionKBManager } from './collection-kb-manager.js';
import { openVoiceManager } from './voice-manager.js';
import { openAnalytics } from './analytics-manager.js';

/** Menu state */
let menuOpen = false;

/** Menu DOM elements (initialized after DOM ready) */
let menuElements = {
    hamburger: null,
    overlay: null,
    panel: null,
    closeBtn: null,
    agentSelect: null,       // Hidden select for backwards compatibility
    collectionSelect: null,  // Hidden select for backwards compatibility
    modelSelect: null,       // Hidden select for backwards compatibility
    agentSelectorBtn: null,       // New modal trigger button
    collectionSelectorBtn: null,  // New modal trigger button
    modelSelectorBtn: null,       // New modal trigger button
    currentAgentDisplay: null,
    currentCollectionDisplay: null,
    loadBtn: null,
    newChatBtn: null,
    tempSlider: null,
    tempValue: null,
    fidelitySlider: null,
    fidelityValue: null,
    memorySlider: null,
    memoryValue: null
};

/**
 * Initialize menu elements and event listeners
 */
export function initMenu() {
    // Get menu elements
    menuElements.hamburger = document.getElementById('hamburger-btn');
    menuElements.overlay = document.getElementById('menu-overlay');
    menuElements.panel = document.getElementById('menu-panel');
    menuElements.closeBtn = document.getElementById('menu-close-btn');

    // Hidden selects for backwards compatibility
    menuElements.agentSelect = document.getElementById('menu-agent-select');
    menuElements.collectionSelect = document.getElementById('menu-collection-select');
    menuElements.modelSelect = document.getElementById('menu-model-select');

    // New modal trigger buttons
    menuElements.agentSelectorBtn = document.getElementById('agent-selector-btn');
    menuElements.collectionSelectorBtn = document.getElementById('collection-selector-btn');
    menuElements.modelSelectorBtn = document.getElementById('model-selector-btn');
    menuElements.currentAgentDisplay = document.getElementById('current-agent-display');
    menuElements.currentCollectionDisplay = document.getElementById('current-collection-display');

    menuElements.loadBtn = document.getElementById('menu-load-btn');
    menuElements.newChatBtn = document.getElementById('menu-new-chat-btn');
    menuElements.tempSlider = document.getElementById('temp-slider');
    menuElements.tempValue = document.getElementById('temp-value');

    // Initialize temperature slider from saved state
    if (menuElements.tempSlider && menuElements.tempValue) {
        const savedTemp = state.temperature ?? 0.7;
        menuElements.tempSlider.value = savedTemp;
        menuElements.tempValue.textContent = savedTemp.toFixed(1);
        menuElements.tempSlider.addEventListener('input', handleTemperatureChange);
    }

    // Initialize fidelity slider from saved state
    menuElements.fidelitySlider = document.getElementById('fidelity-slider');
    menuElements.fidelityValue = document.getElementById('fidelity-value');
    if (menuElements.fidelitySlider && menuElements.fidelityValue) {
        const savedFidelity = state.fidelity ?? 0.7;
        menuElements.fidelitySlider.value = savedFidelity;
        menuElements.fidelityValue.textContent = savedFidelity.toFixed(1);
        menuElements.fidelitySlider.addEventListener('input', handleFidelityChange);
        // Initially disabled until agent/collection is loaded
        updateFidelitySliderState();
    }

    // Initialize memory slider from saved state
    menuElements.memorySlider = document.getElementById('memory-slider');
    menuElements.memoryValue = document.getElementById('memory-value');
    if (menuElements.memorySlider && menuElements.memoryValue) {
        const savedMemory = state.memoryLevel ?? 2;
        menuElements.memorySlider.value = savedMemory;
        menuElements.memoryValue.textContent = getMemoryLevelLabel(savedMemory);
        menuElements.memorySlider.addEventListener('input', handleMemoryChange);
    }

    // Auto-compact toggle (context-meter.js shows what it does)
    const autoCompact = document.getElementById('auto-compact-toggle');
    if (autoCompact) {
        autoCompact.checked = state.autoCompact !== false;
        autoCompact.addEventListener('change', () => {
            setAutoCompact(autoCompact.checked);
            refreshContextMeter();
        });
    }

    // Settings accordion toggle
    const settingsAccordion = document.getElementById('settings-accordion');
    const settingsToggle = document.getElementById('settings-accordion-toggle');
    if (settingsAccordion && settingsToggle) {
        settingsToggle.addEventListener('click', () => {
            settingsAccordion.classList.toggle('expanded');
        });
    }

    // Set up event listeners
    menuElements.hamburger?.addEventListener('click', openMenu);
    menuElements.overlay?.addEventListener('click', closeMenu);
    menuElements.closeBtn?.addEventListener('click', closeMenu);

    // Modal trigger buttons
    menuElements.agentSelectorBtn?.addEventListener('click', openAgentManager);
    menuElements.collectionSelectorBtn?.addEventListener('click', openCollectionManager);
    menuElements.modelSelectorBtn?.addEventListener('click', openModelManager);

    // Initialize agent and collection managers with selection callbacks
    initAgentManager(handleAgentSelected);
    initCollectionManager(handleCollectionSelected);

    // Initialize KB manager
    initKBManager();
    updateKBCountBadge();  // Set initial count on page load
    document.getElementById('kb-manager-btn')?.addEventListener('click', () => {
        closeMenu();
        openKBManager();
    });

    // Analytics Dashboard button
    document.getElementById('analytics-btn')?.addEventListener('click', () => {
        closeMenu();
        openAnalytics();
    });

    // API Keys button (container custom_api mode)
    document.getElementById('api-keys-btn')?.addEventListener('click', async () => {
        closeMenu();
        const { showApiKeyManager } = await import('./api-key-manager.js');
        showApiKeyManager();
    });

    // Leave Container button
    document.getElementById('leave-container-btn')?.addEventListener('click', () => {
        clearContainerMode();
        window.location.reload();
    });

    // Initialize agent/collection KB managers (for managing KB assignments)
    initAgentKBManager();
    initCollectionKBManager();
    document.getElementById('manage-agent-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();  // Don't trigger selector dropdown
        openAgentKBManager();
    });
    document.getElementById('manage-collection-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();  // Don't trigger selector dropdown
        openCollectionKBManager();
    });

    // Voice selector button
    document.getElementById('voice-selector-btn')?.addEventListener('click', openVoiceManager);

    // Agent selection change (hidden select - for backwards compatibility)
    menuElements.agentSelect?.addEventListener('change', handleAgentChange);

    // Collection selection change (hidden select - for backwards compatibility)
    menuElements.collectionSelect?.addEventListener('change', handleCollectionChange);

    // Load button (works for either agent or collection)
    menuElements.loadBtn?.addEventListener('click', handleLoad);

    // New chat button
    menuElements.newChatBtn?.addEventListener('click', handleNewChat);

    // Model selection change (hidden select - for backwards compatibility)
    menuElements.modelSelect?.addEventListener('change', handleModelChange);

    // Close menu on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && menuOpen) {
            closeMenu();
        }
    });

    // Menu panel tooltips (JS-positioned to stay within panel)
    initMenuTooltips();

    // Apply container mode restrictions if in container mode
    if (isContainerMode()) {
        applyContainerModeRestrictions();
    }
}

/**
 * Initialize JS-positioned tooltips for menu panel info icons.
 * These replace the CSS pseudo-element tooltips to ensure proper centering.
 */
function initMenuTooltips() {
    const panel = document.getElementById('menu-panel');
    if (!panel) return;

    function showTooltip(el) {
        removeMenuTooltip();

        const title = el.dataset.tooltipTitle || '';
        const color = el.dataset.tooltipColor || '#e94560';
        const text = el.dataset.tooltip || '';

        const tooltip = document.createElement('div');
        tooltip.className = 'menu-tooltip';
        tooltip.innerHTML = `<strong style="color:${color}">${title}</strong> ${text}`;
        document.body.appendChild(tooltip);

        // Position: below the icon, spanning the panel width
        const iconRect = el.getBoundingClientRect();
        const panelRect = panel.getBoundingClientRect();
        tooltip.style.top = (iconRect.bottom + 8) + 'px';
        tooltip.style.left = (panelRect.left + 16) + 'px';
        tooltip.style.right = (window.innerWidth - panelRect.right + 16) + 'px';

        // Tap tooltip to dismiss
        tooltip.addEventListener('click', (e) => {
            e.stopPropagation();
            removeMenuTooltip();
        });

        // Swipe tooltip to dismiss
        let touchStartX = 0;
        let touchStartY = 0;
        let dragging = false;

        tooltip.addEventListener('touchstart', (e) => {
            touchStartX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
            dragging = true;
            tooltip.style.transition = 'none';
        }, { passive: true });

        tooltip.addEventListener('touchmove', (e) => {
            if (!dragging) return;
            const dx = e.touches[0].clientX - touchStartX;
            const dy = e.touches[0].clientY - touchStartY;
            // Only track horizontal swipes (not vertical scrolling)
            if (Math.abs(dx) > Math.abs(dy)) {
                tooltip.style.transform = `translateX(${dx}px)`;
                tooltip.style.opacity = Math.max(0, 1 - Math.abs(dx) / 150);
            }
        }, { passive: true });

        tooltip.addEventListener('touchend', (e) => {
            if (!dragging) return;
            dragging = false;
            const dx = e.changedTouches[0].clientX - touchStartX;
            tooltip.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
            if (Math.abs(dx) > 60) {
                // Fling away in swipe direction
                tooltip.style.transform = `translateX(${dx > 0 ? 200 : -200}px)`;
                tooltip.style.opacity = '0';
                setTimeout(() => tooltip.remove(), 200);
            } else {
                // Snap back
                tooltip.style.transform = '';
                tooltip.style.opacity = '';
            }
        }, { passive: true });
    }

    panel.querySelectorAll('.info-tooltip[data-tooltip]').forEach(el => {
        // Desktop: hover
        el.addEventListener('mouseenter', () => showTooltip(el));
        el.addEventListener('mouseleave', () => removeMenuTooltip());

        // Mobile: tap to toggle (prevent triggering elements underneath)
        el.addEventListener('touchend', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const existing = document.querySelector('.menu-tooltip');
            if (existing) {
                removeMenuTooltip();
            } else {
                showTooltip(el);
            }
        });
    });

    // Dismiss tooltip when tapping anywhere else on the panel
    panel.addEventListener('touchend', (e) => {
        if (!e.target.closest('.info-tooltip') && !e.target.closest('.menu-tooltip')) {
            removeMenuTooltip();
        }
    });
}

/**
 * Remove any active menu tooltip
 */
function removeMenuTooltip() {
    document.querySelector('.menu-tooltip')?.remove();
}

/**
 * Apply container mode restrictions to the menu
 * - Hide sliders (temperature, fidelity, memory)
 * - Make agent/collection/model selectors read-only
 * - Show configured agent/model from container config
 * - Keep New Chat button functional
 */
export function applyContainerModeRestrictions() {
    // Branch on container mode type
    const config = state.containerConfig;
    if (config && config.mode === 'workspace') {
        applyWorkspaceModeRestrictions();
        return;
    }

    // Demo mode — locked read-only UI
    // Hide slider sections
    const sliderSections = document.querySelectorAll('.menu-section.slider');
    sliderSections.forEach(section => {
        section.style.display = 'none';
    });

    // Also hide the sliders by ID if slider class isn't used
    const slidersToHide = ['temp-slider', 'fidelity-slider', 'memory-slider'];
    slidersToHide.forEach(id => {
        const slider = document.getElementById(id);
        if (slider) {
            const section = slider.closest('.menu-section');
            if (section) section.style.display = 'none';
        }
    });

    // Update display values from container config
    if (config) {
        if (config.agent && menuElements.currentAgentDisplay) {
            const displayName = config.agent
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            menuElements.currentAgentDisplay.textContent = displayName;
        }
        if (config.model) {
            const modelDisplay = document.getElementById('current-model-display');
            if (modelDisplay) {
                // Show a friendly model name
                let friendlyName = config.model;
                // Extract from OpenRouter path if needed
                if (friendlyName.includes('/')) {
                    friendlyName = friendlyName.split('/').pop().split(':')[0];
                }
                // Make it pretty: "llama-3.3-70b-instruct" -> "Llama 3.3 70B"
                friendlyName = friendlyName
                    .replace(/-instruct$/, '')
                    .replace(/-/g, ' ')
                    .replace(/(\d+)b\b/gi, '$1B')
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
                modelDisplay.textContent = friendlyName;
            }
        }

        // Also update header status to show model
        updateHeaderStatus();
    }

    // Make selector buttons look disabled (but don't disable them - keep for display)
    const selectorBtns = [
        menuElements.agentSelectorBtn,
        menuElements.collectionSelectorBtn,
        menuElements.modelSelectorBtn
    ];
    selectorBtns.forEach(btn => {
        if (btn) {
            btn.style.pointerEvents = 'none';
            btn.style.opacity = '0.7';
            btn.classList.add('container-readonly');
        }
    });

    // Hide the Load button (can't change agent/model in container mode)
    if (menuElements.loadBtn) {
        menuElements.loadBtn.style.display = 'none';
    }

    // Hide Knowledge Bases section (container users shouldn't manage KBs)
    const kbNavSection = document.querySelector('.kb-nav-section');
    if (kbNavSection) {
        kbNavSection.style.display = 'none';
    }

    // Hide the KB manage buttons on agent/collection selectors
    const manageAgentBtn = document.getElementById('manage-agent-btn');
    const manageCollectionBtn = document.getElementById('manage-collection-btn');
    if (manageAgentBtn) manageAgentBtn.style.display = 'none';
    if (manageCollectionBtn) manageCollectionBtn.style.display = 'none';

    // Show the "Leave Container" button if allowed by container config
    if (state.containerConfig?.allow_leave !== false) {
        const leaveBtn = document.getElementById('leave-container-btn');
        if (leaveBtn) leaveBtn.style.display = 'inline-flex';
    }

    // Show API Keys button if container uses custom API keys
    if (state.containerConfig?.custom_api) {
        const apiKeysBtn = document.getElementById('api-keys-btn');
        if (apiKeysBtn) apiKeysBtn.style.display = 'inline-flex';
    }
}

/**
 * Apply workspace container mode restrictions.
 *
 * Workspace mode — full-featured blank app with model whitelist.
 * Most UI is functional (agents, collections, KBs, sliders, voice).
 * Only system-level admin operations are restricted.
 */
export function applyWorkspaceModeRestrictions() {
    // Workspace mode keeps most UI functional — nothing to hide
    // The model selector will automatically only show whitelisted models
    // since the server filters /models/all by allowed_models

    // Show the "Leave Container" button if allowed by container config
    if (state.containerConfig?.allow_leave !== false) {
        const leaveBtn = document.getElementById('leave-container-btn');
        if (leaveBtn) leaveBtn.style.display = 'inline-flex';
    }

    // Show API Keys button if container uses custom API keys
    if (state.containerConfig?.custom_api) {
        const apiKeysBtn = document.getElementById('api-keys-btn');
        if (apiKeysBtn) apiKeysBtn.style.display = 'inline-flex';
    }
}

/**
 * Handle temperature slider change
 */
function handleTemperatureChange() {
    const value = parseFloat(menuElements.tempSlider.value);
    menuElements.tempValue.textContent = value.toFixed(1);
    setTemperature(value);
}

/**
 * Handle fidelity slider change
 */
function handleFidelityChange() {
    const value = parseFloat(menuElements.fidelitySlider.value);
    menuElements.fidelityValue.textContent = value.toFixed(1);
    setFidelity(value);
}

/**
 * Get display label for memory level
 */
function getMemoryLevelLabel(level) {
    const labels = ['Off', 'Basic', 'Full'];
    return labels[level] || 'Full';
}

/**
 * Handle memory slider change
 */
function handleMemoryChange() {
    const value = parseInt(menuElements.memorySlider.value, 10);
    menuElements.memoryValue.textContent = getMemoryLevelLabel(value);
    setMemoryLevel(value);
    refreshContextMeter();
}

/**
 * Update fidelity slider enabled/disabled state based on agent/collection selection
 */
export function updateFidelitySliderState() {
    if (!menuElements.fidelitySlider) return;

    // Fidelity makes sense when agent or collection is selected (or loaded)
    // Check both selected (state from modals) and loaded (in memory) states
    const hasKbContext = state.loadedAgent || state.loadedCollection ||
                         state.currentAgent || state.currentCollection ||
                         menuElements.agentSelect?.value || menuElements.collectionSelect?.value;
    menuElements.fidelitySlider.disabled = !hasKbContext;

    // Visual feedback - dim the label when disabled
    const fidelitySection = menuElements.fidelitySlider.closest('.menu-section');
    if (fidelitySection) {
        fidelitySection.style.opacity = hasKbContext ? '1' : '0.5';
    }
}

/**
 * Apply agent-level slider overrides to the admin panel sliders.
 * Called after an agent loads when its settings have override values.
 */
export function applyAgentSliderOverrides(settings) {
    if (!settings) return;

    if (settings.agent_temperature !== null && settings.agent_temperature !== undefined) {
        const temp = settings.agent_temperature;
        setTemperature(temp);
        if (menuElements.tempSlider) menuElements.tempSlider.value = temp;
        if (menuElements.tempValue) menuElements.tempValue.textContent = temp.toFixed(1);
    }

    if (settings.agent_memory_level !== null && settings.agent_memory_level !== undefined) {
        const mem = settings.agent_memory_level;
        setMemoryLevel(mem);
        if (menuElements.memorySlider) menuElements.memorySlider.value = mem;
        if (menuElements.memoryValue) menuElements.memoryValue.textContent = getMemoryLevelLabel(mem);
    }

    if (settings.agent_fidelity !== null && settings.agent_fidelity !== undefined) {
        const fid = settings.agent_fidelity;
        setFidelity(fid);
        if (menuElements.fidelitySlider) menuElements.fidelitySlider.value = fid;
        if (menuElements.fidelityValue) menuElements.fidelityValue.textContent = fid.toFixed(1);
    }
}

/**
 * Handle model selection change
 * Note: This only updates UI state, NOT state.loadedModel
 * Model is only "loaded" when user clicks Load button
 */
function handleModelChange() {
    const selectedModel = menuElements.modelSelect.value;

    // Sync with hidden model select
    if (elements.modelSelect) {
        elements.modelSelect.value = selectedModel;
    }

    // Update load button and header display
    updateMenuLoadButton();
    updateHeaderStatus();

    // Don't save to sessionStorage here - only save when model is actually loaded
    // This prevents the bug where dropdown selection overwrites the actual loaded model
}

/**
 * Open the slide-out menu
 */
export function openMenu() {
    menuOpen = true;
    menuElements.panel?.classList.add('open');
    menuElements.overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Refresh conversations list when menu opens
    refreshConversations();

    // Update KB count badge (skip in container mode - KB section is hidden)
    if (!isContainerMode()) {
        updateKBCountBadge();
    }
}

/**
 * Update KB count badge in menu
 */
async function updateKBCountBadge() {
    const count = await getKBCount();
    const badge = document.getElementById('kb-count-badge');
    if (badge) badge.textContent = count;
}

/**
 * Close the slide-out menu
 */
export function closeMenu() {
    menuOpen = false;
    menuElements.panel?.classList.remove('open');
    menuElements.overlay?.classList.remove('active');
    document.body.style.overflow = '';
    removeMenuTooltip();
}

/**
 * Update dropdown disabled states - disable one when the other is selected
 */
function updateDropdownStates() {
    const agentSelected = menuElements.agentSelect?.value;
    const collectionSelected = menuElements.collectionSelect?.value;

    // Disable collection dropdown when agent is selected
    if (menuElements.collectionSelect) {
        menuElements.collectionSelect.disabled = !!agentSelected;
    }

    // Disable agent dropdown when collection is selected
    if (menuElements.agentSelect) {
        menuElements.agentSelect.disabled = !!collectionSelected;
    }
}

/**
 * Handle agent selection change - clears collection selection
 */
function handleAgentChange() {
    const selectedAgent = menuElements.agentSelect.value;

    // Clear collection selection (mutually exclusive)
    if (selectedAgent && menuElements.collectionSelect) {
        menuElements.collectionSelect.value = '';
        state.currentCollection = '';
        state.loadedCollection = null;
    }

    // Update dropdown disabled states
    updateDropdownStates();

    // Sync with main elements reference
    if (elements.agentSelect) {
        elements.agentSelect.value = selectedAgent;
    }

    // Reset loaded state when agent selection changes
    if (state.loadedAgent !== selectedAgent) {
        state.loadedAgent = null;
    }
    state.currentAgent = selectedAgent;
    updateMenuLoadButton();
    updateFidelitySliderState();  // Enable/disable fidelity based on selection
    updateModeIndicators(state.loadedAgent, state.loadedCollection, state.narratorMode, state.narratorState.chunk_id);

    // Reset narrator mode when switching agents
    if (state.narratorMode) {
        resetNarratorState();
        updateModeIndicators(state.loadedAgent, state.loadedCollection, false, 1);
        import('./voice.js').then(({ updateVoiceBtnIcon }) => {
            updateVoiceBtnIcon(false, false);
        }).catch(() => {});
    }

    // Update header display
    updateHeaderStatus();
}

/**
 * Handle collection selection change - clears agent selection
 */
function handleCollectionChange() {
    const selectedCollection = menuElements.collectionSelect.value;

    // Clear agent selection (mutually exclusive)
    if (selectedCollection && menuElements.agentSelect) {
        menuElements.agentSelect.value = '';
        state.currentAgent = '';
        state.loadedAgent = null;
        // Reset narrator mode when switching to collection
        if (state.narratorMode) {
            resetNarratorState();
        }
    }

    // Update dropdown disabled states
    updateDropdownStates();

    // Reset loaded state when collection selection changes
    if (state.loadedCollection !== selectedCollection) {
        state.loadedCollection = null;
    }
    state.currentCollection = selectedCollection;
    updateMenuLoadButton();
    updateFidelitySliderState();  // Enable/disable fidelity based on selection
    updateModeIndicators(state.loadedAgent, state.loadedCollection, state.narratorMode, state.narratorState.chunk_id);

    // Update header display
    updateHeaderStatus();
}

/**
 * Handle agent selected from Agent Manager modal
 * @param {string|null} agentName - Selected agent name, or null to clear
 * @param {Object|null} agentData - Agent data from API
 */
function handleAgentSelected(agentName, agentData) {
    if (agentName === null) {
        // Clear agent selection
        state.currentAgent = '';
        state.loadedAgent = null;
        state.agentSettings = null;
        state.isLightAgent = false;
        if (menuElements.agentSelect) menuElements.agentSelect.value = '';
        if (elements.agentSelect) elements.agentSelect.value = '';
        updateAgentDisplay(null);

        // Reset narrator mode when clearing agent
        if (state.narratorMode) {
            resetNarratorState();
        }

        // Remove tool toggles from plus menu and clear tooling state
        window.populateToolToggles?.([]);
        setToolingRequired(false);
    } else {
        // Set agent selection (clears collection - mutually exclusive)
        state.currentAgent = agentName;
        state.currentCollection = '';
        state.loadedCollection = null;
        if (menuElements.agentSelect) menuElements.agentSelect.value = agentName;
        if (menuElements.collectionSelect) menuElements.collectionSelect.value = '';

        // Format display name
        const displayName = agentName
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        updateAgentDisplay(displayName);
        updateCollectionDisplay(null);

        // Reset narrator mode when switching
        if (state.narratorMode) {
            resetNarratorState();
        }
    }

    updateMenuLoadButton();
    updateFidelitySliderState();
    updateModeIndicators(state.loadedAgent, state.loadedCollection, state.narratorMode, state.narratorState.chunk_id);
    updateHeaderStatus();

    // Reset voice button icon when agent changes (narrator → mic, etc.)
    window.updateNarratorMenuSection?.();
    import('./voice.js').then(({ updateVoiceBtnIcon }) => {
        const effectiveNarrator = state.narratorMode && !state.narratorPaused;
        updateVoiceBtnIcon(effectiveNarrator, !!(state.agentSettings?.narrator_verbatim));
    }).catch(() => {});
}

/**
 * Handle collection selected from Collection Manager modal
 * @param {string|null} collectionName - Selected collection name, or null to clear
 * @param {Object|null} collectionData - Collection data from API
 */
function handleCollectionSelected(collectionName, collectionData) {
    if (collectionName === null) {
        // Clear collection selection
        state.currentCollection = '';
        state.loadedCollection = null;
        if (menuElements.collectionSelect) menuElements.collectionSelect.value = '';
        updateCollectionDisplay(null);
    } else {
        // Set collection selection (clears agent - mutually exclusive)
        state.currentCollection = collectionName;
        state.currentAgent = '';
        state.loadedAgent = null;
        if (menuElements.collectionSelect) menuElements.collectionSelect.value = collectionName;
        if (menuElements.agentSelect) menuElements.agentSelect.value = '';

        // Use title if available
        const displayName = collectionData?.title || collectionName
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        updateCollectionDisplay(displayName);
        updateAgentDisplay(null);

        // Store title for loading modal
        if (collectionData?.title && menuElements.collectionSelect) {
            const option = menuElements.collectionSelect.querySelector(`option[value="${collectionName}"]`);
            if (option) option.dataset.collectionTitle = collectionData.title;
        }

        // Reset narrator mode when switching
        if (state.narratorMode) {
            resetNarratorState();
        }
    }

    updateMenuLoadButton();
    updateFidelitySliderState();
    updateModeIndicators(state.loadedAgent, state.loadedCollection, state.narratorMode, state.narratorState.chunk_id);
    updateHeaderStatus();
}

/**
 * Update agent display button text
 */
export function updateAgentDisplay(displayName) {
    if (menuElements.currentAgentDisplay) {
        menuElements.currentAgentDisplay.textContent = displayName || 'No Agent';
    }
    if (menuElements.agentSelectorBtn) {
        menuElements.agentSelectorBtn.classList.toggle('has-selection', !!displayName);
    }
}

/**
 * Update collection display button text
 */
export function updateCollectionDisplay(displayName) {
    if (menuElements.currentCollectionDisplay) {
        menuElements.currentCollectionDisplay.textContent = displayName || 'No Collection';
    }
    if (menuElements.collectionSelectorBtn) {
        menuElements.collectionSelectorBtn.classList.toggle('has-selection', !!displayName);
    }
}

/**
 * Handle Load button click - works for agent, collection, or model
 * Also handles Reload - clears chat and starts fresh
 */
async function handleLoad() {
    const agent = state.currentAgent || menuElements.agentSelect?.value;
    const collection = state.currentCollection || menuElements.collectionSelect?.value;
    // Check for model selected from Model Manager first, then fall back to dropdown
    const selectedModel = state.selectedModel || menuElements.modelSelect?.value;

    // A pending model change takes priority over agent/collection "Reload".
    // Picking a new model while an agent/collection conversation is open is a
    // real model switch — ask whether to keep the conversation or start a new
    // one, instead of silently reloading the agent into a fresh conversation.
    const pendingModel = state.selectedModelData
        ? (state.selectedModelData.tier || state.selectedModelData.id)
        : (selectedModel && !state.selectedModelData ? selectedModel : null);
    const modelChanged = pendingModel && pendingModel !== state.loadedModel;
    const agentChanged = agent && agent !== state.loadedAgent;
    const collectionChanged = collection && collection !== state.loadedCollection;

    if (modelChanged && !agentChanged && !collectionChanged) {
        if (state.sessionId) {
            const name = state.selectedModelData?.display_name || selectedModel || 'the selected model';
            const scope = await chooseModelSwitchScope(name);
            if (scope === 'cancel') {
                updateHeaderStatus();
                return;
            }
            if (scope === 'new') {
                // Fresh conversation, keeping the current agent/collection
                // context — only the model changes.
                state.sessionId = null;
                resetNarratorState();
                clearActiveConversation();
                clearChat();
            }
            // 'here' → keep the current session, just swap the model
        }
        closeMenu();
        if (state.selectedModel && state.selectedModelData) {
            await loadSelectedModel();
        } else {
            await loadModelHandler(selectedModel);
        }
        updateHeaderStatus();
        return;
    }

    // Check if this is a reload (already loaded)
    const isReload = (agent && agent === state.loadedAgent) ||
                     (collection && collection === state.loadedCollection);

    if (isReload) {
        // Clear chat and reset session for reload
        state.sessionId = null;
        resetNarratorState();
        clearChat();
    }

    if (agent) {
        // Sync selection to main elements
        if (elements.agentSelect) {
            elements.agentSelect.value = agent;
        }

        // Clear conversation selection and session when loading new agent
        clearActiveConversation();
        state.sessionId = null;

        closeMenu();
        await loadAgentHandler();
    } else if (collection) {
        // Clear conversation selection and session when loading new collection
        clearActiveConversation();
        state.sessionId = null;

        closeMenu();
        await loadCollectionHandler(collection);
    } else if (state.selectedModel && state.selectedModelData) {
        // Model selected from Model Manager - use loadSelectedModel
        closeMenu();
        await loadSelectedModel();
    } else if (selectedModel && selectedModel !== state.loadedModel) {
        // Model selected from dropdown (legacy path)
        closeMenu();
        await loadModelHandler(selectedModel);
    }

    updateHeaderStatus();
}

/**
 * Handle model-only loading into VRAM
 */
async function loadModelHandler(modelTier) {
    // Show connecting overlay with loading message
    const overlay = document.getElementById('connecting-overlay');
    const text = overlay?.querySelector('.connecting-text');
    // Use dropdown's data-provider (set by model-manager from server data) as source of truth
    const selectedOption = menuElements.modelSelect?.selectedOptions[0];
    const optionProvider = selectedOption?.dataset?.provider;
    const isAnthropicTier = optionProvider === 'anthropic' || ['haiku', 'sonnet', 'opus'].includes(modelTier);
    const isGoogleTier = optionProvider === 'google' || modelTier?.startsWith('gemini-');
    const isOpenAITier = optionProvider === 'openai' || modelTier?.startsWith('gpt-') || ['o3', 'o3-mini', 'o4-mini'].includes(modelTier);
    const isGroqTier = optionProvider === 'groq';
    const isOpenRouterTier = optionProvider === 'openrouter' || ['r1', 'deepseek'].includes(modelTier);
    const isApiTier = isAnthropicTier || isGoogleTier || isOpenAITier || isGroqTier || isOpenRouterTier || modelTier?.includes('/');
    if (overlay) {
        overlay.classList.remove('hidden');
        if (text) {
            // API tiers don't need model loading - just connecting
            if (isApiTier) {
                text.textContent = isAnthropicTier ? 'Connecting to Claude API' :
                    isGoogleTier ? 'Connecting to Gemini' :
                    isOpenAITier ? 'Connecting to OpenAI' :
                    isGroqTier ? 'Connecting to Groq' : 'Connecting to API';
            } else {
                // Use model name (Qwen, Llama, etc.) not tier
                const modelName = selectedOption?.dataset?.model || modelTier;
                text.textContent = `Loading ${modelName}`;
            }
        }
    }

    try {
        await loadModel(modelTier);
        state.loadedModel = modelTier;
        updateMenuLoadButton();
        updateHeaderStatus();

        // Update the current model display
        const displayName = selectedOption?.dataset?.model || modelTier;
        const provider = isAnthropicTier ? 'anthropic'
            : isGoogleTier ? 'google'
            : isOpenAITier ? 'openai'
            : isGroqTier ? 'groq'
            : isOpenRouterTier ? 'openrouter'
            : isApiTier ? 'openrouter'
            : 'local';
        updateCurrentModelDisplay(modelTier, displayName, provider, true);

        // Hide overlay
        if (overlay) overlay.classList.add('hidden');
        const message = isApiTier ? 'Connected to API. Ready to chat.' : 'Model loaded. Ready to chat.';
        addSystemMessage(message);
    } catch (e) {
        console.error('[Menu] Model load failed:', e);
        if (overlay) overlay.classList.add('hidden');
        addSystemMessage('Failed to load model.');
    }
}

/**
 * Handle New Chat button click
 */
function handleNewChat() {
    closeMenu();
    commands.new();
    updateHeaderStatus();
}

/**
 * Update header status display
 * Updates model indicator in the mode-indicators section
 */
export function updateHeaderStatus() {
    // Clear the header-status (no longer used for model)
    const statusEl = document.getElementById('header-status');
    if (statusEl) statusEl.textContent = '';

    // Update model indicator in mode-indicators
    const indicatorsEl = document.getElementById('mode-indicators');
    if (!indicatorsEl) return;

    // Remove existing model indicator
    const existingModel = indicatorsEl.querySelector('.mode-indicator.model');
    if (existingModel) existingModel.remove();

    // Verbatim narration reads the book aloud out of the knowledge base and
    // never asks a model anything. Naming one here is a claim about work that
    // is not happening, and with Extract Facts off nothing in this mode needs
    // a model at all. Paused is the exception: a paused narrator answers
    // questions, and that is a real model doing real work.
    if (state.narratorMode && !state.narratorPaused && state.agentSettings?.narrator_verbatim) {
        return;
    }

    // Check if we have a model loaded
    const loadedModel = state.loadedModel;
    // Use state.loadedProvider as the source of truth (set on load and persisted in session)
    // Fall back to dropdown's data-provider for backwards compat
    const modelSelect = menuElements.modelSelect;
    const selectedOpt = modelSelect?.selectedOptions[0];
    const optProvider = honestProvider(loadedModel, state.loadedProvider || selectedOpt?.dataset?.provider);
    const isAnthropicTier = optProvider === 'anthropic' || (loadedModel && ['haiku', 'sonnet', 'opus'].includes(loadedModel));
    const isGoogle = optProvider === 'google' || (loadedModel && loadedModel.startsWith('gemini-'));
    const isOpenAI = optProvider === 'openai' || (loadedModel && (loadedModel.startsWith('gpt-') || ['o3', 'o3-mini', 'o4-mini'].includes(loadedModel)));
    const isGroq = optProvider === 'groq';
    const isOpenRouterTier = optProvider === 'openrouter' || (loadedModel && ['r1', 'deepseek'].includes(loadedModel));
    const isCloudModel = loadedModel && (isAnthropicTier || isGoogle || isOpenAI || isGroq || isOpenRouterTier || loadedModel.includes('/') || loadedModel.startsWith('claude-'));

    // Build a labeled chip: [MODEL | <value>] to match the agent chip's
    // two-span structure. label + divider live in CSS, JS just supplies
    // the two spans.
    const buildModelChip = (value) => {
        const span = document.createElement('span');
        span.className = 'mode-indicator model';
        span.title = value;
        const label = document.createElement('span');
        label.className = 'mode-indicator__label';
        label.textContent = 'Model';
        const val = document.createElement('span');
        val.className = 'mode-indicator__value';
        val.textContent = value;
        span.append(label, val);
        return span;
    };

    if (isCloudModel) {
        // Cloud model (Anthropic, Google, OpenAI, Groq, or OpenRouter)
        const displayName = getModelDisplayName(loadedModel);
        let provider = 'OpenRouter';
        if (isAnthropicTier) {
            provider = 'Anthropic';
        } else if (isGoogle) {
            provider = 'Google';
        } else if (isOpenAI) {
            provider = 'OpenAI';
        } else if (isGroq) {
            provider = 'Groq';
        }
        indicatorsEl.appendChild(buildModelChip(`${provider} (${displayName})`));
    } else if (modelSelect?.value) {
        // Local model - use dropdown selection
        const selectedOption = modelSelect.options[modelSelect.selectedIndex];
        const tier = selectedOption?.dataset?.tier || modelSelect.value;
        const model = selectedOption?.dataset?.model || '';
        const displayText = model ? `${tier} (${model})` : tier;
        indicatorsEl.appendChild(buildModelChip(displayText));
    } else if (loadedModel) {
        // Loaded model the dropdown can't represent — e.g. a worker-only
        // model (vLLM pinned lane) loaded via the model manager. The chip
        // must reflect state, not the dropdown, or it vanishes on refresh.
        indicatorsEl.appendChild(
            buildModelChip(getModelDisplayName(loadedModel) || loadedModel)
        );
    }
}

/**
 * Populate agent dropdown in menu
 */
export function populateMenuAgents(agents) {
    if (!menuElements.agentSelect) return;

    menuElements.agentSelect.innerHTML = '<option value="">No Agent</option>';
    agents.forEach(agent => {
        const option = document.createElement('option');
        option.value = agent.name;
        option.textContent = agent.name;
        menuElements.agentSelect.appendChild(option);
    });
}

/**
 * Populate collection dropdown in menu
 */
export function populateMenuCollections(collections) {
    if (!menuElements.collectionSelect) return;

    menuElements.collectionSelect.innerHTML = '<option value="">No Collection</option>';
    collections.forEach(coll => {
        const option = document.createElement('option');
        option.value = coll.name;
        option.textContent = `${coll.title} (${coll.kb_count} KBs)`;
        option.title = coll.description || '';  // Tooltip on hover
        option.dataset.collectionTitle = coll.title;  // Store title for loading modal
        menuElements.collectionSelect.appendChild(option);
    });
}

/**
 * Get the selected collection's title from the dropdown
 */
export function getSelectedCollectionTitle() {
    if (!menuElements.collectionSelect) return null;
    const selectedOption = menuElements.collectionSelect.selectedOptions[0];
    return selectedOption?.dataset?.collectionTitle || null;
}

/**
 * Get collection title by collection name (for conversation list display)
 */
export function getCollectionTitleByName(collectionName) {
    if (!menuElements.collectionSelect || !collectionName) return null;
    const options = menuElements.collectionSelect.options;
    for (let i = 0; i < options.length; i++) {
        if (options[i].value === collectionName) {
            return options[i].dataset?.collectionTitle || null;
        }
    }
    return null;
}

/**
 * Populate model dropdown in menu
 */
export function populateMenuModels(tiers, poolInfo = null) {
    if (poolInfo) setPoolModelInfo(poolInfo);
    if (!menuElements.modelSelect) return;

    menuElements.modelSelect.innerHTML = '';
    for (const [tierName, tierInfo] of Object.entries(tiers)) {
        const option = document.createElement('option');
        option.value = tierName;
        // Capitalize tier name
        const tierDisplay = tierName.charAt(0).toUpperCase() + tierName.slice(1);
        // Remove "(TierName)" suffix from display_name if present
        let modelDisplay = tierInfo.display_name || tierName;
        modelDisplay = modelDisplay.replace(/\s*\([^)]*\)\s*$/, '').trim();
        option.textContent = `${tierDisplay} (${modelDisplay})`;
        option.dataset.tier = tierDisplay;
        option.dataset.model = modelDisplay;
        option.dataset.modelName = tierInfo.model_name; // Actual Ollama model name for reverse lookup
        // A model that exists to describe images is never a chat default — the
        // server already refuses to pick one, and healUnservableModel needs the
        // same fact to stop a conversation re-pinning one it was opened with.
        if (tierInfo.vision_only) option.dataset.visionOnly = '1';
        if (!tierInfo.installed) {
            option.textContent += ' - not installed';
            option.disabled = true;
        }
        menuElements.modelSelect.appendChild(option);
    }
}

/** What the pool is serving right now, from the last /models fetch. */
let poolServingModels = [];
let poolDefaultModel = null;

export function setPoolModelInfo({ serving_models, pool_default_model } = {}) {
    if (Array.isArray(serving_models)) poolServingModels = serving_models;
    if (pool_default_model) poolDefaultModel = pool_default_model;
}

/**
 * A servable stand-in for `model`, or null when `model` is already fine.
 *
 * A conversation stores the model it was last used with, and opening it
 * re-pins the header to that name. That is right until the crew serving it is
 * stood down: the name then survives every refresh, the header shows a model
 * nothing can run, and Retry sends it again because Retry reads the header.
 * Live: the 9b crew was switched off and the 27b switched on, yet reloading
 * the conversation put qwen3.5:9b back in the header three times running, and
 * each send was refused in about a millisecond before reaching any worker.
 *
 * Only heals LOCAL models. A cloud model is served by its provider, not by
 * this pool, so its absence from the serving list means nothing.
 */
export function healUnservableModel(model, provider = 'local') {
    if (!model || (provider && provider !== 'local')) return null;
    // No pool picture yet (delegation off, or /models not back). Assume fine —
    // guessing here would fight the user's choice on every cold start.
    if (!poolServingModels.length) return null;

    const modelName = modelNameForValue(model) || model;
    const served = poolServingModels.includes(modelName) || poolServingModels.includes(model);
    // Being served is not enough: a vision specialist is served perfectly well
    // and is still the wrong thing to hand a chat. Live case — a narrator
    // conversation remembered qwen3-vl:2b, the pool was serving exactly that
    // and nothing else, so every turn went to a 2B image describer while a
    // cloud chat lane sat switched on. The server refuses to DEFAULT to one;
    // this is the same refusal applied to what a conversation remembers.
    if (served && !isVisionOnlyValue(model)) return null;
    // Already the pool's own answer: nothing to heal, and healing it to itself
    // would announce that it is not being served, which it is.
    if (model === poolDefaultModel || modelName === poolDefaultModel) return null;

    // Prefer the pool's own default — that is the server's answer to exactly
    // this question, cloud lane included — then anything else it serves. Never
    // another image describer.
    for (const candidate of [poolDefaultModel, ...poolServingModels]) {
        if (!candidate) continue;
        const tier = findTierByModelName(candidate);
        if (tier && !isVisionOnlyValue(tier)) return { value: tier, modelName: candidate };
        // The pool default is often a cloud model with no local tier row. It is
        // still the right answer: hand back the name itself.
        if (!tier && candidate === poolDefaultModel) {
            return { value: candidate, modelName: candidate };
        }
    }
    return null;
}

/** Is this dropdown value a model that exists to describe images? */
export function isVisionOnlyValue(value) {
    if (!menuElements.modelSelect || !value) return false;
    for (const option of menuElements.modelSelect.options) {
        if (option.value === value || option.dataset.modelName === value) {
            return option.dataset.visionOnly === '1';
        }
    }
    return false;
}

/**
 * The provider a remembered model really has. A cloud provider next to a
 * model the dropdown knows as a worker model is a leftover from whatever was
 * loaded before it, so it is corrected to "local"; anything else is passed
 * through. Not only OpenRouter: after a turn on the Groq cloud lane, the
 * header went on reading "Groq (Bonsai2 27B)" once the crew was back, and a
 * cloud provider also cuts the stream timeout to 90 s, which a crew model
 * thinking before its first word can outlast.
 */
const CLOUD_PROVIDER_NAMES = new Set(['openrouter', 'groq', 'anthropic', 'openai', 'google']);
export function honestProvider(model, provider) {
    if (!model || !CLOUD_PROVIDER_NAMES.has(provider)) return provider || null;
    const known = providerForValue(model);
    if (known) return known;
    const name = modelNameForValue(model) || model;
    if (name.includes('/') || ['r1', 'deepseek'].includes(model)) return provider;
    if (findTierByModelName(model)) return 'local';
    // An Ollama tag ("bonsai2:27b") is a worker model whatever was remembered
    // beside it; no cloud id is written that way except an explicit "groq:".
    if (name.includes(':') && !name.startsWith('groq:')) return 'local';
    return provider;
}

/** Which provider serves a dropdown value, when the dropdown knows. */
export function providerForValue(value) {
    if (!menuElements.modelSelect || !value) return null;
    for (const option of menuElements.modelSelect.options) {
        if (option.value === value || option.dataset.modelName === value) {
            return option.dataset.provider || null;
        }
    }
    return null;
}

/** The real model name behind a dropdown value (tier alias or name). */
export function modelNameForValue(value) {
    if (!menuElements.modelSelect || !value) return null;
    for (const option of menuElements.modelSelect.options) {
        if (option.value === value) return option.dataset.modelName || null;
    }
    return null;
}

/**
 * Find tier value by actual Ollama model name
 */
export function findTierByModelName(modelName) {
    if (!menuElements.modelSelect || !modelName) return null;

    // Check by actual model name (e.g., "qwen2.5:14b", "claude-opus-4-20250514")
    for (const option of menuElements.modelSelect.options) {
        if (option.dataset.modelName === modelName) {
            return option.value;
        }
    }
    // Also check by tier value (e.g., "opus", "small") since that's what state.loadedModel stores
    for (const option of menuElements.modelSelect.options) {
        if (option.value === modelName) {
            return option.value;
        }
    }
    return null;
}

/**
 * Get display name by actual model name (e.g., "nvidia/nemotron..." -> "Nemotron 3 Nano 30B")
 */
export function getModelDisplayName(modelName) {
    if (!modelName) return null;

    // First check dropdown options
    if (menuElements.modelSelect) {
        for (const option of menuElements.modelSelect.options) {
            if (option.dataset.modelName === modelName) {
                return option.dataset.model;
            }
        }
    }

    // Known Groq model display names
    const groqNames = {
        'llama-3.3-70b-versatile': 'Llama 3.3 70B',
        'llama-3.1-70b-versatile': 'Llama 3.1 70B',
        'llama-3.1-8b-instant': 'Llama 3.1 8B',
        'mixtral-8x7b-32768': 'Mixtral 8x7B',
        'gemma2-9b-it': 'Gemma 2 9B',
        'meta-llama/llama-4-scout-17b-16e-instruct': 'Llama 4 Scout',
        'openai/gpt-oss-20b': 'GPT-OSS 20B',
        'openai/gpt-oss-120b': 'GPT-OSS 120B',
        'qwen/qwen3-32b': 'Qwen3 32B',
        'moonshotai/kimi-k2-instruct-0905': 'Kimi K2',
    };
    if (groqNames[modelName]) {
        return groqNames[modelName];
    }
    // Handle groq: prefix (e.g., "groq:llama-3.3-70b-versatile")
    if (modelName.startsWith('groq:')) {
        const stripped = modelName.slice(5);
        if (groqNames[stripped]) return groqNames[stripped];
    }

    // Known OpenRouter model display names
    const openRouterNames = {
        'nvidia/nemotron-3-nano-30b-a3b:free': 'Nemotron 3 Nano 30B',
        'nvidia/nemotron-nano-12b-v2-vl:free': 'Nemotron Nano 12B VL',
        'meta-llama/llama-3.3-70b-instruct:free': 'Llama 3.3 70B',
        'google/gemma-3-27b-it:free': 'Gemma 3 27B',
        'google/gemini-2.0-flash-exp:free': 'Gemini 2.0 Flash',
        'deepseek/deepseek-r1-0528:free': 'DeepSeek R1',
        'mistralai/devstral-2512:free': 'Devstral',
        'qwen/qwen3-coder:free': 'Qwen3 Coder',
        'nousresearch/hermes-3-llama-3.1-405b:free': 'Hermes 3 405B',
        'cognitivecomputations/dolphin-mistral-24b-venice-edition:free': 'Dolphin Mistral 24B',
    };
    if (openRouterNames[modelName]) {
        return openRouterNames[modelName];
    }

    // Fallback: prettify without destroying identity. An ollama-style tag
    // ("qwen3.8:27b") IS the model's name — family, version and size — so
    // keep all of it: "Qwen3.8 27B". Reducing it to the leading letters
    // turned the header chip into a bare "Qwen" on refresh, which reads
    // as some other model entirely.
    if (!modelName.includes('/')) {
        const [base, size] = modelName.split(':');
        const pretty = base.charAt(0).toUpperCase() + base.slice(1);
        return size ? `${pretty} ${size.toUpperCase()}` : pretty;
    }
    // Hub paths ("nvidia/nemotron-3-nano-30b-a3b:free") keep the short
    // family name — the full path is too long for a chip.
    const match = modelName.match(/\/([a-zA-Z]+)/);
    if (match) {
        const name = match[1];
        return name.charAt(0).toUpperCase() + name.slice(1);
    }
    return modelName;
}

/**
 * Update load button state in menu - works for agent, collection, or model
 */
export function updateMenuLoadButton() {
    if (!menuElements.loadBtn) return;

    // Use state values (from modals) or fall back to hidden select values
    const selectedAgent = state.currentAgent || menuElements.agentSelect?.value;
    const selectedCollection = state.currentCollection || menuElements.collectionSelect?.value;
    const selectedModel = state.selectedModel || menuElements.modelSelect?.value;

    // A pending model change (picked in the Model Manager) is always a real
    // "Load", never a "Reload" — even when the agent/collection is unchanged.
    const pendingModel = state.selectedModelData
        ? (state.selectedModelData.tier || state.selectedModelData.id)
        : null;
    if (pendingModel && pendingModel !== state.loadedModel) {
        menuElements.loadBtn.textContent = 'Load';
        menuElements.loadBtn.disabled = false;
        return;
    }

    if (selectedAgent && selectedAgent === state.loadedAgent) {
        menuElements.loadBtn.textContent = 'Reload';
        menuElements.loadBtn.disabled = false;
    } else if (selectedCollection && selectedCollection === state.loadedCollection) {
        menuElements.loadBtn.textContent = 'Reload';
        menuElements.loadBtn.disabled = false;
    } else if (selectedAgent || selectedCollection) {
        menuElements.loadBtn.textContent = 'Load';
        menuElements.loadBtn.disabled = false;
    } else if (selectedModel) {
        // Only model selected - show "Load <Model>"
        // Use selectedModelData if available (from Model Manager), otherwise fall back to dropdown
        const modelData = state.selectedModelData;
        const selectedOption = menuElements.modelSelect?.selectedOptions?.[0];
        const displayName = modelData?.display_name || selectedOption?.dataset?.model || selectedModel;

        if (selectedModel === state.loadedModel && !state.selectedModel) {
            // Model is already loaded (and no new selection pending)
            menuElements.loadBtn.textContent = `Loaded`;
            menuElements.loadBtn.disabled = true;
        } else {
            menuElements.loadBtn.textContent = `Load`;
            menuElements.loadBtn.disabled = false;
        }
    } else {
        menuElements.loadBtn.textContent = 'Load';
        menuElements.loadBtn.disabled = true;
    }
}

/**
 * Get menu elements (for external access)
 */
export function getMenuElements() {
    return menuElements;
}
